/** One row of the active-users roster for a document (collapsed to one entry per user). */
export interface DocumentRosterParticipant {
  userId: string;
  displayName: string;
  upn: string | null;
  hasPhoto: boolean | null;
  connections: number;
  isEditing?: boolean;
  isTyping?: boolean;
  lastTypingActivityUtc?: string | null;
}

/** Exclusive edit lock holder for a document (null when unlocked). */
export interface DocumentEditLockHolder {
  userId: string;
  displayName: string;
  connectionId: string;
  acquiredAtUtc: string;
}

/** Pushed when another user's save-back to storage completes. */
export interface DocumentFileSavedNotice {
  savedByUserId: string;
  savedByDisplayName: string;
  savedBySessionId: string;
  versionStamp: string | null;
  savedAtUtc: string;
}

export interface ForceReloadOptions {
  /** Show a loading overlay for peer collaboration reloads (avoids a blank flash). */
  overlay?: boolean;
}

export const DOCUMENT_COLLAB_HUB_PATH = '/hubs/document-collab';

export const TYPING_INDICATOR_MS = 1500;

/** Whether a roster row should show the typing animation (uses server flag or local expiry). */
export function participantIsTyping(
  participant: DocumentRosterParticipant,
  nowMs: number = Date.now()
): boolean {
  const stamp = participant.lastTypingActivityUtc;
  if (stamp) {
    const ms = Date.parse(stamp);
    if (Number.isFinite(ms)) return nowMs - ms < TYPING_INDICATOR_MS;
  }
  return Boolean(participant.isTyping);
}

export function formatEditLockHolderName(holder: DocumentEditLockHolder | null | undefined): string {
  const name = (holder?.displayName ?? '').trim();
  return name || 'Another user';
}

/** True when the lock belongs to this browser tab / signed-in identity. */
export function isEditLockHeldBySelf(
  holder: DocumentEditLockHolder | null | undefined,
  options: {
    connectionId?: string | null;
    currentUserId?: string | null;
    currentUserAliases?: Iterable<string> | null;
  }
): boolean {
  if (!holder) return false;

  const myConnectionId = (options.connectionId ?? '').trim();
  const holderConnectionId = holder.connectionId.trim();
  if (holderConnectionId) {
    // Locks are connection-scoped. A matching identity in another tab cannot release this lock and
    // must not be shown tab-level actions such as Stop Editing.
    return Boolean(myConnectionId && holderConnectionId === myConnectionId);
  }

  // Identity is only a compatibility fallback for legacy holders that did not include a connection.
  const holderId = holder.userId.trim().toLowerCase();
  if (!holderId) return false;

  const ids = new Set<string>();
  const primary = (options.currentUserId ?? '').trim();
  if (primary && primary.toLowerCase() !== 'unknown') ids.add(primary.toLowerCase());
  if (options.currentUserAliases) {
    for (const alias of options.currentUserAliases) {
      const value = (alias ?? '').trim().toLowerCase();
      if (value && value !== 'unknown') ids.add(value);
    }
  }
  return ids.has(holderId);
}
